// Notes on the company record (REQ-2026-10-07-167; module 04 B2354; migration 252). Mike, 7 Oct 2026: "there seems to be
// no way to add notes to the customers file remembering we need to log who added the note"; by pop-up, "Shared always".
//
// A note is typed here, or added as a file in any format (notes/AddNotes.tsx, U209). Either way it goes up through the
// notes route against this company, so the store keeps who added it and when, and its words are read as that person's
// own. Every note anyone may read is listed, newest first, each with who added it and when. A note of the reader's own
// that Cobrain has not yet read for privacy is theirs alone until it has (R26), and says so; nobody else's is shown in
// its place.
//
// The box and its list are NotesBox, drawn on a deal card as well (deals/DealNotes.tsx, REQ-2026-10-08-13).
import { ActionTextarea, ActionButton } from '../parts/index.js';
import { viewFetch as fetch } from '../view-as.js';
import { useRef, useState, type ReactNode } from 'react';
import { Unauthenticated } from '../api.js';
import { notesApi } from '../notes/api.js';
import { AddNotes } from '../notes/AddNotes.js';
import { uploadedWords, whenWords } from '../notes/words.js';
import { threadPath } from '../threads/api.js';
import { useLoad, type Load } from './shared';
import '../notes/notes.css';

export interface CompanyNote {
  id: string;
  name: string;
  kind: string;
  state: 'waiting' | 'reading' | 'read' | 'refused' | 'failed';
  reason: string | null;
  addedAt: string;
  addedBy: { principalId: string; name: string | null };
  mine: boolean;
  words: string | null;
  cut: boolean;
  signalId: string | null;
  privateYet: boolean;
  hasPage: boolean;
}

export async function readCompanyNotes(store: string, companyId: string): Promise<{ notes: CompanyNote[]; total: number }> {
  const r = await fetch(`/api/records/companies/${encodeURIComponent(companyId)}/notes?${new URLSearchParams({ store }).toString()}`, { credentials: 'same-origin' });
  if (r.status === 401) throw new Unauthenticated();
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const j = (await r.json()) as { notes: CompanyNote[]; total: number };
  return { notes: j.notes, total: j.total };
}

/** The most a typed note may be; a longer one goes up as a file. */
export const TYPED_NOTE_MAX = 20000;

/** "Added by Mike Cobain, 7 Oct, 21:10", or "Added by you, ..." for the reader's own. */
export function addedWords(n: Pick<CompanyNote, 'mine' | 'addedBy' | 'addedAt'>): string {
  return `Added by ${n.mine ? 'you' : n.addedBy.name?.trim() || 'a colleague'}, ${whenWords(n.addedAt)}`;
}

/** What a note says in place of its words while it has none to show. */
export function noteStatusWords(n: Pick<CompanyNote, 'state' | 'reason' | 'kind' | 'words'>): string | null {
  if (n.words) return null;
  switch (n.state) {
    case 'waiting': case 'reading': return n.kind === 'audio' ? 'Kept; audio notes are not read yet.' : 'Being read.';
    case 'refused': return `Not read: ${n.reason ?? 'the page could not be read'}.`;
    case 'failed': return 'Not read. Upload it again later.';
    case 'read': return 'Cobrain holds no words for this note.';
  }
}

export function CompanyNotes({ storeId, companyId, companyName, open }: { storeId: string; companyId: string; companyName: string; open: (path: string) => void }) {
  const [load, reload] = useLoad(() => readCompanyNotes(storeId, companyId), [storeId, companyId], { keep: true });
  const total = load.kind === 'ready' ? load.data.total : 0;
  return (
    <section className="sec co-notes" id="a-notes">
      <div className="sh">
        <h2>Notes</h2>
        <span className="sl">{load.kind === 'ready' ? notesCountWords(total) : ''}</span>
      </div>
      <div className="sb">
        <NotesBox storeId={storeId} on={{ company: companyId }} idBase={`co-note-${companyId}`} label={<>Add a note to {companyName}&rsquo;s file</>}
          fileName={`Note on ${companyName}.txt`} load={load} reload={reload} open={open} />
      </div>
    </section>
  );
}

/** "None yet", "One note", "3 notes". */
export const notesCountWords = (total: number): string => (total === 0 ? 'None yet' : total === 1 ? 'One note' : `${total.toLocaleString('en-GB')} notes`);

type NotesLoad = Load<{ notes: CompanyNote[]; total: number }>;

/**
 * The note box and the list under it: type a note and save it, or add one as a file in any format, then every note the
 * reader may read, newest first, each with who added it and when. Drawn on the company record (CompanyNotes) and on a
 * deal card (deals/DealNotes.tsx, REQ-2026-10-08-13); `on` is what the notes are added on, and `after` says, under the
 * box, what happens to a note once it is saved.
 */
export function NotesBox({ storeId, on, idBase, label, fileName, load, reload, open, after = null }: {
  storeId: string; on: { company: string } | { deal: string }; idBase: string; label: ReactNode; fileName: string;
  load: NotesLoad; reload: () => void; open: (path: string) => void; after?: ReactNode;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; tone: 'plain' | 'warn' } | null>(null);
  // Held from the first press until the save answers, as Add task is (BUGS.md TK37): a second press saves nothing twice.
  const saving = useRef(false);
  const target = 'company' in on ? { company: on.company } : { deal: on.deal };

  const save = async () => {
    const words = text.trim();
    if (!words || saving.current) return;
    saving.current = true;
    setBusy(true);
    setSaid(null);
    try {
      const u = await notesApi.upload(storeId, new File([words], fileName.slice(0, 200), { type: 'text/plain' }), target);
      const w = uploadedWords('The note', u);
      setSaid(w);
      if (u.ok && (u.state === 'read' || u.state === 'waiting')) { setText(''); setSaid({ text: 'Your note is added.', tone: 'plain' }); }
    } catch {
      setSaid({ text: 'The note was not added. Nothing was saved. Try again.', tone: 'warn' });
    } finally {
      saving.current = false;
      setBusy(false);
      reload();
    }
  };

  const notes = load.kind === 'ready' ? load.data.notes : [];
  const total = load.kind === 'ready' ? load.data.total : 0;

  return (
    <>
      <form className="co-note-add" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <label className="co-note-label" htmlFor={idBase}>{label}</label>
        <ActionTextarea id={idBase} className="co-note-text" rows={3} maxLength={TYPED_NOTE_MAX} value={text}
          onChange={(e) => setText(e.target.value)} placeholder="What was said, agreed or learned" disabled={busy} />
        <div className="co-note-go">
          <ActionButton type="submit" className="opt lead" disabled={busy || text.trim() === ''}>{busy ? 'Saving the note' : 'Save the note'}</ActionButton>
          <AddNotes store={storeId} companyId={'company' in on ? on.company : null} dealId={'deal' in on ? on.deal : null} onAdded={reload} />
        </div>
        {said ? <p className={`co-note-said${said.tone === 'warn' ? ' warn' : ''}`} role="status">{said.text}</p> : null}
        <p className="read">Your colleagues see a note once Cobrain has read it for privacy, with who added it and when.</p>
        {after}
      </form>

      {load.kind === 'error' ? <p className="none" role="alert">The notes did not load. <ActionButton reading type="button" className="hx-link" onClick={reload}>Try again</ActionButton></p> : null}
      {notes.length
        ? (
          <ol className="co-note-list">
            {notes.map((n) => {
              const status = noteStatusWords(n);
              const path = n.signalId ? threadPath(n.signalId, storeId) : null;
              return (
                <li key={n.id} className="co-note">
                  <p className="co-note-by"><b>{addedWords(n)}</b>{n.kind !== 'text' ? <span>, from {n.name}</span> : null}</p>
                  {n.words ? <div className="co-note-words">{n.words}</div> : null}
                  {status ? <p className="none">{status}</p> : null}
                  {n.privateYet ? <p className="none">Only you see this until Cobrain has read it for privacy.</p> : null}
                  {(n.cut && path) || (n.hasPage && n.mine)
                    ? (
                      <p className="co-note-more">
                        {n.cut && path ? <a className="go" href={path} onClick={(e) => { e.preventDefault(); open(path); }}>Read it whole</a> : null}
                        {n.hasPage && n.mine ? <>{n.cut && path ? ' ' : ''}<a className="go" href={notesApi.pageUrl(storeId, n.id)} target="_blank" rel="noreferrer">Open the page you added</a></> : null}
                      </p>
                    )
                    : null}
                </li>
              );
            })}
          </ol>
        )
        : null}
      {total > notes.length ? <p className="none">The newest {notes.length} of {total.toLocaleString('en-GB')} are listed.</p> : null}
    </>
  );
}
